import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {TrustedHTML, TrustedTypePolicy} from '@github-ui/trusted-types'
// eslint-disable-next-line no-restricted-imports -- This IS the sanitize facade; it wraps dompurify.
import DOMPurify, {type Config as DOMPurifyConfig} from 'dompurify'

import {addMarkdownSanitizeHooks, MARKDOWN_SANITIZE_CONFIG} from './configs/markdown'
import type {SafeHTMLString, SanitizedHTML} from './safe-html-string'
import {getMarkdownSanitizePolicy, getSanitizePolicy, mintMarkdownTrustedHTML, mintTrustedHTML} from './trusted-types'
import type {PresetConfig, Purifier, SanitizeConfig} from './types'

/**
 * Sanitize HTML using DOMPurify with the browser's native window.
 *
 * This is the browser entry point — on the server, the "node" condition
 * in package.json exports resolves to sanitize.server.ts instead.
 *
 * String results are routed through the private `github-sanitize-lax`
 * Trusted Types policy when the `trusted_types_policies` feature
 * flag is on and `window.trustedTypes` is available, so that the output
 * can be safely assigned to TrustedHTML sinks in CSP-`trusted-types`-
 * enforced contexts.
 *
 * @param html - The HTML string to sanitize
 * @param config - Optional DOMPurify Config object, or a named preset
 *   (`{preset: 'markdown'}`). A preset fully defines behavior and cannot be
 *   combined with DOMPurify overrides.
 */
export function sanitize(dirty: string | Node, cfg: PresetConfig): SanitizedHTML
export function sanitize(dirty: string | Node, cfg: DOMPurifyConfig & {RETURN_DOM: true; preset?: never}): Node
export function sanitize(
  dirty: string | Node,
  cfg: DOMPurifyConfig & {RETURN_DOM_FRAGMENT: true; preset?: never},
): DocumentFragment
export function sanitize(
  dirty: string | Node,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: true; preset?: never},
): TrustedHTML
export function sanitize(
  dirty: string | Node,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: false; preset?: never},
): SafeHTMLString
export function sanitize(dirty: string | Node, cfg?: DOMPurifyConfig & {preset?: never}): SanitizedHTML
export function sanitize(dirty: string | Node, cfg?: SanitizeConfig): SanitizedHTML | Node | DocumentFragment {
  // A named preset fully owns its config, hooks, and Trusted Types policy —
  // route it to the dedicated preset path, ignoring the default purifier.
  if (isPresetConfig(cfg)) {
    switch (cfg.preset) {
      case 'markdown':
        return sanitizeMarkdown(dirty)
    }
  }
  // sanitize() is sanitizeWithPurifier() bound to the module-level DOMPurify singleton.
  return sanitizeWithPurifier(dirty, DOMPurify as unknown as Purifier, cfg)
}

/**
 * Narrow a config union to its preset arm. A raw DOMPurify config never carries
 * a `preset` key (enforced by the `SanitizeConfig` type), so a present `preset`
 * unambiguously selects the preset path.
 */
function isPresetConfig(cfg: SanitizeConfig | undefined): cfg is PresetConfig {
  return cfg != null && 'preset' in cfg && cfg.preset != null
}

// The markdown preset's purifier is a DOMPurify instance carrying the markdown
// security hooks. Built lazily on first markdown use and memoized for the page
// lifetime, mirroring the module-level default singleton.
let markdownPurifier: Purifier | undefined
function getMarkdownPurifier(): Purifier {
  markdownPurifier ??= createPurifier(addMarkdownSanitizeHooks)
  return markdownPurifier
}

/**
 * Sanitize through the `markdown` preset: the hardened markdown config + hooks,
 * minted through the dedicated `github-sanitize-markdown` Trusted Types policy.
 * Unlike the default path this ignores the `sanitize_dompurify_hardening` flag —
 * the caller explicitly opted into hardening by selecting the preset.
 */
function sanitizeMarkdown(dirty: string | Node): SanitizedHTML {
  const policy = getMarkdownSanitizePolicy()
  // Inject the markdown policy so DOMPurify's internal minting stays on
  // `github-sanitize-markdown` rather than lazily creating its own `dompurify` policy.
  const effectiveCfg = policy
    ? {...MARKDOWN_SANITIZE_CONFIG, TRUSTED_TYPES_POLICY: policy as TrustedTypePolicy}
    : MARKDOWN_SANITIZE_CONFIG
  const result = getMarkdownPurifier().sanitize(dirty, effectiveCfg)
  return mintMarkdownTrustedHTML(result)
}

/**
 * Create a DOMPurify instance for advanced usage (e.g., adding hooks).
 * The returned purifier's `.sanitize()` is typed to return `SafeHTMLString`
 * for standard string sanitization calls.
 */
export function createPurifier(cb?: (purifier: Purifier) => void): Purifier {
  const purifier = DOMPurify() as unknown as Purifier
  if (cb) {
    cb(purifier)
  }
  return purifier
}

export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_DOM: true},
): Node
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_DOM_FRAGMENT: true},
): DocumentFragment
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: true},
): TrustedHTML
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg: DOMPurifyConfig & {RETURN_TRUSTED_TYPE: false},
): SafeHTMLString
export function sanitizeWithPurifier(dirty: string | Node, purifier: Purifier, cfg?: DOMPurifyConfig): SanitizedHTML
export function sanitizeWithPurifier(
  dirty: string | Node,
  purifier: Purifier,
  cfg?: DOMPurifyConfig,
): SanitizedHTML | Node | DocumentFragment {
  // TODO: When hardening is implemented (#1724, #1725), this flag will gate
  // the DOMPurify hooks that strip js-* classes and block data-* attributes.
  const _hardeningEnabled = isFeatureEnabled('sanitize_dompurify_hardening')
  const effectiveCfg = withSanitizePolicy(cfg)
  const result = purifier.sanitize(dirty, effectiveCfg)
  // DOM / DocumentFragment / explicit Trusted Type return modes bypass the
  // string-wrapping path — DOMPurify already handled them.
  if (cfg && (cfg.RETURN_DOM || cfg.RETURN_DOM_FRAGMENT || cfg.RETURN_TRUSTED_TYPE)) {
    return result
  }
  // Caller explicitly opted out of Trusted Types — return the sanitized string
  // without minting a policy value, regardless of the feature flag. DOMPurify
  // already produced a plain string.
  if (cfg?.RETURN_TRUSTED_TYPE === false) {
    return result
  }
  return mintTrustedHTML(result)
}

/**
 * Inject our `github-sanitize-lax` policy as DOMPurify's `TRUSTED_TYPES_POLICY`
 * whenever it's available (flag on + Trusted Types available), overriding any
 * caller-supplied policy to enforce the single-named-policy guarantee.
 *
 * DOMPurify 3.4.0 mints through a Trusted Types policy on every parse, so
 * without one it lazily creates its own (non-allowlisted) `dompurify` policy.
 * Injecting on the default path — not just `RETURN_TRUSTED_TYPE: true` — keeps
 * that internal minting on `github-sanitize-lax`. The exception is an explicit
 * `RETURN_TRUSTED_TYPE: false`: the caller opted out and wants a plain string.
 */
function withSanitizePolicy<T extends DOMPurifyConfig | undefined>(cfg: T): T {
  if (cfg?.RETURN_TRUSTED_TYPE === false) {
    return cfg
  }
  const policy = getSanitizePolicy()
  if (!policy) {
    return cfg
  }
  return {...cfg, TRUSTED_TYPES_POLICY: policy as TrustedTypePolicy}
}

// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {SafeHTMLString, SanitizedHTML} from './safe-html-string'
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type {Config, Purifier, UponSanitizeAttributeHookEvent} from './types'
